import { TinyPopover, Toolbar } from '@octopus-do/octopus-storybook'
import { Globe, Link } from '@octopus-do/octopus-storybook/icons'
import type React from 'react'
import { forwardRef } from 'react'

import { isValidEmail, isValidUrl } from '@/helpers'
import { useInputGuard } from '../../hooks'

// Key the popover reports to Toolbar.Root; the submitted url follows it,
// or REMOVE_LINK_KEY when the link is removed.
export const LINK_POPOVER_ID = 'link'
export const REMOVE_LINK_KEY = 'remove'

const isLink = (value: string) => isValidUrl(value) || isValidEmail(value)

interface ContentProps {
  onRemove: () => void
  url: string
}

const Content: React.FC<ContentProps> = ({ onRemove, url }) => (
  <TinyPopover.LinkInput
    icon={Globe}
    onRemove={onRemove}
    placeholder="Paste URL..."
    validate={isLink}
    value={url}
  />
)

interface TriggerProps {
  disabled: boolean
  nodeId: string
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ disabled, nodeId }, ref) => {
    const { active, toggle } = TinyPopover.useTrigger()
    useInputGuard(nodeId, active)

    return (
      <Toolbar.DropdownButton
        active={active}
        disabled={disabled}
        icon={Link}
        onClick={toggle}
        ref={ref}
        tooltip="External link"
      />
    )
  }
)

interface Props {
  disabled: boolean
  nodeId: string
  url: string
}

export const LinkPopover: React.FC<Props> = ({ disabled, nodeId, url }) => {
  const { onClick } = Toolbar.useToolbarClick(LINK_POPOVER_ID)

  const onSubmit = (value: string) => onClick([value])
  const onRemove = () => onClick([REMOVE_LINK_KEY])

  return (
    <TinyPopover.Root
      content={Content}
      onRemove={onRemove}
      onSubmit={onSubmit}
      url={url}
    >
      <Trigger disabled={disabled} nodeId={nodeId} />
    </TinyPopover.Root>
  )
}
